import type { BucketItemSnapshot, BucketAddInput } from "./bucket-types";

export interface BucketState {
  items: BucketItemSnapshot[];
  hydrated: boolean;
  syncing: boolean;
}

export type BucketAction =
  | { type: "hydrate"; items: BucketItemSnapshot[] }
  | { type: "set"; items: BucketItemSnapshot[] }
  | { type: "add-local"; item: BucketItemSnapshot }
  | { type: "upsert-server"; item: BucketItemSnapshot; localId?: string }
  | { type: "patch"; id: string; patch: Partial<BucketItemSnapshot> }
  | { type: "remove"; id: string }
  | { type: "clear" }
  | { type: "syncing"; syncing: boolean };

/**
 * Two items collide if every (activitySlug, packageId, date, time) field matches.
 * Mirrors the server-side partial unique index, so re-adding the same configuration
 * upserts instead of duplicating.
 */
export function isSameLine(a: BucketItemSnapshot, b: BucketAddInput): boolean {
  return (
    a.activitySlug === b.activitySlug &&
    a.packageId === b.packageId &&
    a.date === b.date &&
    (a.time ?? null) === (b.time ?? null)
  );
}

export const initialState: BucketState = {
  items: [],
  hydrated: false,
  syncing: false,
};

export function bucketReducer(state: BucketState, action: BucketAction): BucketState {
  switch (action.type) {
    case "hydrate":
      return { ...state, items: action.items, hydrated: true };
    case "set":
      return { ...state, items: action.items };
    case "add-local":
      return { ...state, items: [action.item, ...state.items.filter((it) => it.id !== action.item.id)] };
    case "upsert-server": {
      // Replace local-* twin (matched by config) plus any prior server entry with the same id.
      const items = state.items.filter((it) => {
        if (it.id === action.item.id) return false;
        if (action.localId && it.id === action.localId) return false;
        return true;
      });
      return { ...state, items: [action.item, ...items] };
    }
    case "patch":
      return {
        ...state,
        items: state.items.map((it) =>
          it.id === action.id ? { ...it, ...action.patch } : it,
        ),
      };
    case "remove":
      return { ...state, items: state.items.filter((it) => it.id !== action.id) };
    case "clear":
      return { ...state, items: [] };
    case "syncing":
      return { ...state, syncing: action.syncing };
  }
}
